import React, { useState, useContext, useMemo, useEffect } from 'react';
import DateTimePickerComponent from '../Components/DateTimePickerComponent';
import SimpleSelect from '../Components/SimpleSelect';
import SimpleTextInput from '../Components/SimpleTextInput';
import KebabInput from '../Components/KebabInput';
import TextAndSelectInput from '../Components/TextAndSelectInput';
import NeenorAutocomplete from '../Components/NeenorAutocomplete';
import { TransportModal, EnsRequestorModal, RoutingModal } from '../Modals/TransportAndJourneyModals';
import { Button } from '@mui/material';
import { useStepper } from '../../../components/Stepper';
import { GBENSContext } from '../GBENSContainer';

const TransportAndJourney = () => {
  const [ensRequestorModalOpen, setEnsRequestorModalOpen] = useState(false);
  const [transportModalOpen, setTransportModalOpen] = useState(false);
  const [routingModalOpen, setRoutingModalOpen] = useState(false);

  const { gotoStep } = useStepper();

  const {
    navToConsignmentId,
    setNavToConsignmentId,
    referenceData,
    data,
    handleShowWarningModal,
    addNewConsignment,
    editConsignment,
    hasConsignments,
    updateData,
    isEditable,
    isReadOnly,
    EUROShuttleDisableFields,
  } = useContext(GBENSContext);

  const screen = 'TransportAndJourney';
  const disableFields = isReadOnly || !isEditable;

  //Navigate to consignment if navigating to consignment from dashboard
  useEffect(() => {
    if (navToConsignmentId && hasConsignments) {
      editConsignment(navToConsignmentId);
      setNavToConsignmentId(false);
      gotoStep('UpdatingConsignment');
    }
  }, [navToConsignmentId, hasConsignments]);

  useEffect(() => {
    // If the ENSRequestor is not set, initialize it with the current declarant's details
    if (!data[screen]?.ENSRequestor?.companyName && referenceData?.declarant?.length) {
      const declarantInfo = referenceData?.declarant[0] || {};
      console.log('Declarant Info:', declarantInfo);
      updateData(
        {
          target: {
            value: {
              companyName: declarantInfo.company,
              EORI: declarantInfo.eori,
              streetAddress: declarantInfo.streetAddress,
              country: declarantInfo.country,
              townCity: declarantInfo.townOrCity,
              postcode: declarantInfo.postcode,
            },
          },
        },
        screen,
        'ENSRequestor'
      );
    }
  }, [referenceData]);

  const onNext = async () => {
    if (disableFields) {
      gotoStep('Consignments');
    } else {
      handleShowWarningModal(screen, () => {
        if (hasConsignments) {
          gotoStep('Consignments');
        } else {
          addNewConsignment();
          gotoStep('AddingConsignment');
        }
      });
    }
  };

  const routeDisplayText = useMemo(() => {
    if (!data[screen]?.Routing) {
      const stopKeys = [
        'departureCountry',
        'transCountry1',
        'transCountry2',
        'transCountry3',
        'transCountry4',
        'transCountry5',
        'transCountry6',
        'transCountry7',
      ];
      const stopValues = stopKeys.map((s) => data[screen]?.Route?.[s]);
      const anyStops = stopValues.filter((s) => !!s && s !== -1);
      if (!anyStops || anyStops.length === 0) return null;
      const resolvedStops = anyStops.map((s) => referenceData.countryOfOriginMap?.find((c) => c.key === s)?.value);
      return (
        resolvedStops.join(' > ') +
        ' > ' +
        referenceData.countryOfOriginMap?.find((c) => c.key === data[screen]?.Route?.destinationCountry)?.value
      );
    }
  }, [data, referenceData]);

  return (
    <>
      <TransportModal screen={screen} open={transportModalOpen} setOpen={setTransportModalOpen} />
      <EnsRequestorModal screen={screen} open={ensRequestorModalOpen} setOpen={setEnsRequestorModalOpen} />
      <RoutingModal screen={screen} open={routingModalOpen} setOpen={setRoutingModalOpen} />

      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <KebabInput
          name="Declarant*"
          value={`${data[screen]?.ENSRequestor?.['companyName']} (${data[screen].ENSRequestor['EORI']})`}
          onClick={() => setEnsRequestorModalOpen(true)}
          id="ENSRequestor"
          screen={screen}
          disabled={disableFields}
        />

        <SimpleTextInput
          name="Job Reference*"
          screen={screen}
          id="jobReference"
          placeholder="Enter your job or shipment reference"
          maxLength={35}
          disabled={disableFields}
        />

        <KebabInput
          name="Transport Identity*"
          value={data[screen]?.Vehicle?.vehicleID}
          placeholder="Click link to add"
          linkText="Add Transport"
          onClick={() => setTransportModalOpen(true)}
          id="Vehicle"
          screen={screen}
          disabled={disableFields}
        />

        {parseInt(data?.TransportAndJourney?.Vehicle?.ModeOfTransport) === 1 && (
          <>
            <NeenorAutocomplete
              name="Groupage Consignor*"
              screen={screen}
              modal="Consignor"
              placeholder="Type to find, or add new consignor details"
              id="Consignor"
              options={referenceData.consignors}
              consignment={false}
              disabled={disableFields}
            />

            <NeenorAutocomplete
              name="Groupage Consignee*"
              screen={screen}
              modal="Consignee"
              placeholder="Type to find, or add new consignee details"
              id="Consignee"
              options={referenceData.consignees}
              consignment={false}
              disabled={disableFields}
            />
          </>
        )}

        <NeenorAutocomplete
          name="Carrier*"
          screen={screen}
          modal="Carrier"
          placeholder="Type to find, or add new carrier details"
          id="Carrier"
          options={referenceData.carriers}
          consignment={false}
          toolTip={
            <span>
              Unaccompanied = Ferry Operator
              <br />
              Accompanied = Haulier
            </span>
          }
          disabled={disableFields}
        />

        <SimpleSelect
          name="Port of Departure"
          screen={screen}
          id="portOfDeparture"
          options={[...(referenceData.exitPorts || [])].sort((a, b) => a.value.localeCompare(b.value))}
          disabled={disableFields}
          isAutocomplete
          allowUserInputAsValue
          placeholder="- Select or Type -"
        />

        <DateTimePickerComponent
          name="Departure Date & Time*"
          screen={screen}
          id="departure"
          toolTip="Timezone = CET"
          disabled={disableFields}
        />

        <SimpleSelect
          name="Port of Arrival*"
          screen={screen}
          id="portOfArrival"
          options={referenceData.entryPorts}
          toolTip="Select the Port of Arrival with the associated customs office"
          disabled={disableFields || EUROShuttleDisableFields}
          isAutocomplete
        />

        <DateTimePickerComponent
          name="Arrival Date & Time*"
          screen={screen}
          id="arrival"
          toolTip="Timezone = CET"
          disabled={disableFields}
        />

        <TextAndSelectInput
          name="Place of Loading*"
          screen={screen}
          id="placeOfLoading"
          options={referenceData.countryOfOriginCC}
          selectPlaceholder="- Country -"
          textPlaceholder="Place name"
          selectId="placeOfLoadingCountry"
          textId="placeOfLoadingName"
          toolTip={
            <span>
              Unaccompanied = Port of Departure
              <br />
              Accompanied = Actual Place of Loading
            </span>
          }
          disabled={disableFields || EUROShuttleDisableFields}
        />
        <TextAndSelectInput
          name="Place of Unloading*"
          screen={screen}
          id="placeOfUnloading"
          options={referenceData.countryOfOriginCC}
          selectPlaceholder="- Country -"
          textPlaceholder="Place name"
          selectId="placeOfUnloadingCountry"
          textId="placeOfUnloadingName"
          toolTip={
            <span>
              Unaccompanied = Port of Arrival
              <br />
              Accompanied = Actual Place of Unloading
            </span>
          }
          disabled={disableFields || EUROShuttleDisableFields}
        />
        <TextAndSelectInput
          name="Place of Acceptance*"
          screen={screen}
          id="placeOfAcceptance"
          options={referenceData.countryOfOriginCC}
          selectPlaceholder="- Country -"
          textPlaceholder="Place name"
          selectId="placeOfAcceptanceCountry"
          textId="placeOfAcceptanceName"
          toolTip="The location where the goods are accepted for transport"
          disabled={disableFields}
        />
        <TextAndSelectInput
          name="Place of Delivery*"
          screen={screen}
          id="placeOfDelivery"
          options={referenceData.countryOfOriginCC}
          selectPlaceholder="- Country -"
          textPlaceholder="Place name"
          selectId="placeOfDeliveryCountry"
          textId="placeOfDeliveryName"
          toolTip="The final location of delivery"
          disabled={disableFields}
        />

        <KebabInput
          name="Route*"
          value={routeDisplayText}
          placeholder="Click link to add"
          id="Route"
          screen={screen}
          linkText="Routing Countries"
          onClick={() => setRoutingModalOpen(true)}
          toolTip="Enter up to 9 routing countries in chronological order"
          disabled={disableFields}
        />

        <TextAndSelectInput
          name="Transport Document*"
          screen={screen}
          id="transportDocument"
          options={referenceData.getDocumentType}
          selectPlaceholder="- Document Type -"
          textPlaceholder="Document Reference"
          selectId="transportDocTypeId"
          textId="transportDocReference"
          toolTip="Reference must be unique and not exist for an accepted shipment or consignment"
          disabled={disableFields}
        />

        <SimpleSelect
          name="Transport Charges MOP*"
          screen={screen}
          id="transportCharges"
          options={referenceData.charges}
          disabled={disableFields}
          isAutocomplete
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
          paddingTop: 30,
        }}
      >
        <div></div>
        <Button variant="outlined" onClick={onNext}>
          Next: Consignments
        </Button>
      </div>
    </>
  );
};

export default TransportAndJourney;
